import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import B2BOrderPromotion from "../components/B2BOrderPromotion";
import B2BPaymentSelector from "../components/B2BPaymentSelector";

const euro = (valore) =>
  Number(valore || 0).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });

function messaggioErrore(data) {
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.error === "string") return data.error;
  if (data && typeof data === "object") {
    return Object.entries(data)
      .map(([campo, valore]) =>
        `${campo}: ${Array.isArray(valore) ? valore.join(", ") : String(valore)}`
      )
      .join(" | ");
  }
  return "Operazione non riuscita.";
}

export default function AppAziendaOrdineRiepilogoPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ordine, setOrdine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [errore, setErrore] = useState("");
  const [successo, setSuccesso] = useState("");

  useEffect(() => {
    let attivo = true;

    async function carica() {
      try {
        const response = await authFetch(`/api/ordini/${id}/`);
        const data = await response.json();

        if (!response.ok) throw new Error(messaggioErrore(data));
        if (attivo) setOrdine(data);
      } catch (error) {
        if (attivo) setErrore(error.message);
      } finally {
        if (attivo) setLoading(false);
      }
    }

    carica();
    return () => { attivo = false; };
  }, [id]);

  async function ricaricaOrdine() {
    const response = await authFetch(
      `/api/ordini/${encodeURIComponent(id)}/`
    );
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(messaggioErrore(data));
    }

    setOrdine(data);
  }

  async function invia() {
    if (!ordine || ordine.status !== "DRAFT" || sending) return;

    if (!["ONLINE", "CONSEGNA"].includes(ordine.payment_method)) {
      setErrore("Seleziona e salva prima il metodo di pagamento.");
      return;
    }

    if (!window.confirm(
      ordine.payment_method === "ONLINE"
        ? "Procedere al pagamento online sicuro con Stripe?"
        : "Confermi l'invio definitivo dell'ordine alla SEDE?"
    )) return;

    setSending(true);
    setErrore("");

    try {
      if (ordine.payment_method === "ONLINE") {
        const response = await authFetch(
          `/api/ordini/${encodeURIComponent(id)}/b2b-stripe/checkout/`,
          { method: "POST" }
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) throw new Error(messaggioErrore(data));

        if (
          typeof data.url !== "string" ||
          !/^https:\/\/checkout\.stripe\.com\//i.test(data.url)
        ) {
          throw new Error("URL Stripe Checkout non valido.");
        }

        window.location.assign(data.url);
        return;
      }

      const response = await authFetch(
        `/api/ordini/${encodeURIComponent(id)}/invia/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) throw new Error(messaggioErrore(data));

      setOrdine((precedente) => ({
        ...precedente,
        status: "SUBMITTED",
      }));
      setSuccesso("Ordine inviato correttamente alla SEDE.");
    } catch (error) {
      setErrore(error.message);
    } finally {
      setSending(false);
    }
  }

  const righe = Array.isArray(ordine?.items)
    ? ordine.items
    : Array.isArray(ordine?.rows)
      ? ordine.rows
      : [];

  return (
    <div className="container py-4" style={{ maxWidth: 850 }}>
      <button
        className="btn btn-link px-0"
        onClick={() => navigate("/app")}
      >
        ← Area Azienda
      </button>

      <h2 className="fw-bold mb-3">Riepilogo ordine #{id}</h2>

      {loading && <p>Caricamento ordine...</p>}

      {errore && (
        <div className="alert alert-danger" role="alert">{errore}</div>
      )}

      {successo && (
        <div className="alert alert-success" role="status">{successo}</div>
      )}

      {ordine && (
        <>
          <div className="card mb-3">
            <div className="card-body">
              <p className="mb-3">
                <strong>Stato:</strong>{" "}
                {ordine.status_it || ordine.status}
              </p>

              {righe.map((r, indice) => {
                const nome =
                  r.product_name ||
                  r.nome_prodotto ||
                  r.product?.nome ||
                  r.product?.name ||
                  `Prodotto ${r.product_id || r.product || indice + 1}`;

                const quantita = Number(r.quantity || 0);
                const unitario = Number(r.unit_price || 0);

                return (
                  <div
                    key={r.id || indice}
                    className="d-flex justify-content-between border-bottom py-3 gap-3"
                  >
                    <div>
                      <strong>{nome}</strong>
                      <div className="text-muted">
                        {quantita} × {euro(unitario)}
                      </div>
                    </div>
                    <strong>
                      {euro(r.line_subtotal ?? quantita * unitario)}
                    </strong>
                  </div>
                );
              })}

              <div className="d-flex justify-content-between mt-4">
                <h4 className="fw-bold">Totale ordine</h4>
                <h4 className="fw-bold">{euro(ordine.total)}</h4>
              </div>
            </div>
          </div>

          <B2BOrderPromotion
            ordine={ordine}
            onUpdated={ricaricaOrdine}
            disabled={sending}
          />

          <B2BPaymentSelector
            ordine={ordine}
            onUpdated={ricaricaOrdine}
            disabled={sending}
          />

          {ordine.status === "DRAFT" && (
            <button
              type="button"
              className="btn btn-dark rounded-pill w-100 py-3"
              disabled={
                sending ||
                !righe.length ||
                !["ONLINE", "CONSEGNA"].includes(ordine.payment_method)
              }
              onClick={invia}
            >
              {sending
                ? ordine.payment_method === "ONLINE"
                  ? "Apertura pagamento..."
                  : "Invio in corso..."
                : ordine.payment_method === "ONLINE"
                  ? "Procedi al pagamento Stripe"
                  : "Conferma e invia alla SEDE"}
            </button>
          )}

          <button
            type="button"
            className="btn btn-outline-secondary rounded-pill w-100 mt-3"
            onClick={() => navigate("/app/azienda/ordini")}
          >
            I miei ordini
          </button>
        </>
      )}
    </div>
  );
}
